import {
  ContentTypes,
  HeaderTypes,
  HttpClientHelper,
  Permission,
  PTVNClient,
} from '@ptvn-react/authentication';
import APIConstant from '../utils/constants/APIConstant';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  MyProfileResponse,
  CompanySubscriptionPlanResponse,
  UserAccountResponse,
  SubscriptionData,
  PackageResponse,
  ConsentDataResponse,
  SearchSupplierUserResponse,
  UnderConstructionDetailResponse,
  SubscriptionMoreInformationResponse as SubscriptionInformationResponse,
  ResponseHistoryResponse,
  CompanySubscriptionPackageSettingResponse,
  ConsentSupplierResponse,
} from './response/AuthorizeEMarketplace';
import {
  PostPricePlanRequest,
  SubscriptionConsentRequest,
  ResubmitRequest,
} from './request/AuthorizeEMarketplaceRequest';
import { PTVNResponse } from './response/BaseResponse';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ConsentType } from '../utils/constants/SubscriptionConstants';
import { BusinessTypeEnum } from '../utils/constants/BusinessTypeConstant';

export class AuthorizeEmarketplaceClient {
  public static getMyProfile(): Observable<MyProfileResponse> {
    const url = `${APIConstant.authorizationEMarketplace.getMyProfile}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getCompanySubscriptionPlan(
    companyID: number,
    roleID: number,
  ): Observable<CompanySubscriptionPlanResponse> {
    const url = `${
      APIConstant.authorizationEMarketplace.getSubscriptionPlan
    }?companyID=${companyID.toString()}&roleID=${roleID}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static postPricePlan(
    postPricePlanObj: PostPricePlanRequest,
    signedConsent: File,
    idCard: File,
    companyRegistration?: File,
    businessRegistrationCert?: File,
    powerAttorney?: File,
    otherFile?: File,
  ): Observable<any> {
    const payload = new FormData();
    payload.append(
      'request',
      new Blob([JSON.stringify(postPricePlanObj)], { type: 'application/json' }),
    );

    payload.append(ConsentType.SIGNED_CONSENT, signedConsent);
    payload.append(ConsentType.COPY_OF_ID_CARD, idCard);
    if (postPricePlanObj.businessType !== BusinessTypeEnum.INDIVIDUAL) {
      companyRegistration && payload.append(ConsentType.COMPANY_REGISTRATION, companyRegistration);
      businessRegistrationCert &&
        payload.append(ConsentType.BUSINESS_REGISTRATION_CERTIFICATE, businessRegistrationCert);
      powerAttorney && payload.append(ConsentType.POWER_OF_ATTORNEY, powerAttorney);
    }
    otherFile && payload.append(ConsentType.OTHER_FILE, otherFile);

    const url = APIConstant.authorizationEMarketplace.postPricePlan;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }

  public static getUserPermissions(
    companyID: number,
    roleID: number,
  ): Observable<PTVNResponse<Permission[]>> {
    const url = `${APIConstant.authorizationEMarketplace.getPermission}?companyID=${companyID}&roleID=${roleID}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static downloadConsentTemplate(): Observable<AxiosResponse<any>> {
    const url = APIConstant.authorizationEMarketplace.downloadConsentTemplate;
    let options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    options = {
      ...options,
      responseType: 'blob',
    };
    return PTVNClient.get(url, options);
  }

  public static getConsentSupplier(
    companyID?: number,
  ): Observable<AxiosResponse<ConsentSupplierResponse>> {
    const url = `${APIConstant.authorizationEMarketplace.getConsentSupplier}?companyID=${companyID}`;
    let options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);

    return PTVNClient.get(url, options);
  }

  public static getUserInCompany(
    companyID: number,
    roleID: number,
  ): Observable<UserAccountResponse[]> {
    const url = `${APIConstant.authorizationEMarketplace.userInCompanyList}?companyID=${companyID}&roleID=${roleID}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static addUser(companyID: number, userID: number, roleID: number): Observable<any[]> {
    const request = {
      userID: userID,
      companyID: companyID,
      roleID: roleID,
    };
    const url = APIConstant.authorizationEMarketplace.addUser;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, request, options).pipe(map((m) => m.data.data));
  }

  public static deleteUser(companyID: number, userID: number, roleID: number): Observable<any> {
    const request = {
      userID: userID,
      companyID: companyID,
      roleID: roleID,
    };
    const url = APIConstant.authorizationEMarketplace.deleteUser;
    let options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(
      HeaderTypes.SSO_TOKEN,
      ContentTypes.JSON,
    );
    options = {
      url: url,
      responseType: 'json',
      method: 'DELETE',
      ...options,
      data: request,
    };
    return PTVNClient.request(options).pipe(map((m) => m.data.data));
  }

  public static getSubscriptionData(companyID: number): Observable<SubscriptionData> {
    const url = `${APIConstant.authorizationEMarketplace.getSubscriptionData.replace(
      '{companyID}',
      companyID.toString(),
    )}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getConsentReadOnly(): Observable<ConsentDataResponse> {
    const url = APIConstant.authorizationEMarketplace.getConentReadOnly;
    return PTVNClient.get(url).pipe(map((m) => m.data.data));
  }

  public static getUserConsent(): Observable<ConsentDataResponse> {
    const url = APIConstant.authorizationEMarketplace.getUserConsent;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static acceptUserConsent(request: SubscriptionConsentRequest): Observable<any> {
    const url = APIConstant.authorizationEMarketplace.acceptUserConsent;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, request, options).pipe(map((m) => m.data));
  }

  public static getPackageList(companyID: number, roleID: number): Observable<PackageResponse[]> {
    const url = `${APIConstant.authorizationEMarketplace.getPackageList}?companyID=${companyID}&roleID=${roleID}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static getSupplierPackageList(): Observable<PackageResponse[]> {
    const url = APIConstant.authorizationEMarketplace.getSupplierPackageList;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data));
  }

  public static searchSupplierUser(
    companyID: number,
    search: string,
    access: boolean,
  ): Observable<SearchSupplierUserResponse> {
    const url = `${APIConstant.authorizationEMarketplace.searchSupplierUser.replace(
      '{companyID}',
      companyID.toString(),
    )}?search=${search}&access=${access}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static addAllSupplierUser(companyID: number): Observable<any> {
    const url = `${APIConstant.authorizationEMarketplace.addAllSupplierUser}`;
    const request = { companyID };
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, request, options).pipe(map((m) => m.data.data));
  }

  public static getUnderConstructionDetail(): Observable<UnderConstructionDetailResponse> {
    const url = `${APIConstant.authorizationEMarketplace.getMaintenanceDetail}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static resubmitDocument(request: ResubmitRequest): Observable<any> {
    const url = APIConstant.authorizationEMarketplace.resubmitDocument;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, request, options).pipe(map((m) => m.data));
  }

  public static getSubscriptionInformation(
    subscriptionID: number,
  ): Observable<SubscriptionInformationResponse> {
    const url = `${APIConstant.authorizationEMarketplace.getSubscriptionInformation.replace(
      '{subscriptionID}',
      subscriptionID.toString(),
    )}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getResponseDocumentHistory(
    subscriptionID: number,
    page: number,
    pageSize: number,
  ): Observable<ResponseHistoryResponse> {
    const url = `${APIConstant.authorizationEMarketplace.getResponseDocumentHistory.replace(
      '{subscriptionID}',
      subscriptionID.toString(),
    )}?page=${page}&pageSize=${pageSize}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static getCompanySubscriptionPackageSetting(
    companyID: number,
  ): Observable<CompanySubscriptionPackageSettingResponse> {
    const url = `${
      APIConstant.authorizationEMarketplace.getSubscriptionPackageSetting
    }?companyID=${companyID.toString()}`;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.get(url, options).pipe(map((m) => m.data.data));
  }

  public static postUpgradeSubscriptionPlan(
    postPricePlanObj: PostPricePlanRequest,
    signedConsent: File,
    idCard: File,
    companyRegistration?: File,
    businessRegistrationCert?: File,
    powerAttorney?: File,
    otherFile?: File,
  ): Observable<any> {
    const payload = new FormData();
    payload.append(
      'request',
      new Blob([JSON.stringify(postPricePlanObj)], { type: 'application/json' }),
    );

    payload.append(ConsentType.SIGNED_CONSENT, signedConsent);
    payload.append(ConsentType.COPY_OF_ID_CARD, idCard);
    if (postPricePlanObj.businessType !== BusinessTypeEnum.INDIVIDUAL) {
      companyRegistration && payload.append(ConsentType.COMPANY_REGISTRATION, companyRegistration);
      businessRegistrationCert &&
        payload.append(ConsentType.BUSINESS_REGISTRATION_CERTIFICATE, businessRegistrationCert);
      powerAttorney && payload.append(ConsentType.POWER_OF_ATTORNEY, powerAttorney);
    }
    otherFile && payload.append(ConsentType.OTHER_FILE, otherFile);

    const url = APIConstant.authorizationEMarketplace.postUpgradeSubscriptionPlan;
    const options: AxiosRequestConfig = HttpClientHelper.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return PTVNClient.post(url, payload, options).pipe(map((m) => m.data));
  }
}
